import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    // CircularProgress,
    Typography,
    Card,
    CardContent,
    // Alert,
    Slider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TimerIcon from '@mui/icons-material/Timer';
import { Link } from 'react-router-dom';
import './AiVideoGen.css';
// import Darkmode from '../components/darkmode/Darkmode.js';

const VIDEO_QUALITIES = [
    { value: '360p', label: '360p - SD Quality', resolution: '640x360' },
    { value: '480p', label: '480p - Enhanced SD', resolution: '854x480' },
    { value: '720p', label: '720p - HD Ready', resolution: '1280x720' },
    { value: '1080p', label: '1080p - Full HD', resolution: '1920x1080' },
];

const ASPECT_RATIOS = [
    { value: '16:9', label: '16:9 - YouTube Landscape' },
    { value: '9:16', label: '9:16 - Shorts' },
    { value: '4:5', label: '4:5 - Instagram' },
];

// Animation settings
const FRAME_RATE = 30;
const TRANSITION_DURATION = 1; // seconds
const WORD_DURATION = 2; // seconds per word

function AiVideoGen() {
    const [prompt, setPrompt] = useState('');
    const [videoQuality, setVideoQuality] = useState('720p');
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [duration, setDuration] = useState(60);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedVideo, setGeneratedVideo] = useState(null);
    const [error, setError] = useState(null);
    const [videoProgress, setVideoProgress] = useState(0);
    const [openPopup, setOpenPopup] = useState(false);

    const canvasRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const speechSynthesisRef = useRef(window.speechSynthesis);

    const generateVideo = async (params) => {
        try {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            // Set canvas dimensions based on quality
            const quality = VIDEO_QUALITIES.find(q => q.value === params.quality);
            const [width, height] = quality.resolution.split('x').map(Number);
            canvas.width = width;
            canvas.height = height;

            // Split prompt into words/sentences
            const words = params.prompt.split(/\s+/);
            const totalFrames = FRAME_RATE * params.duration;
            let currentFrame = 0;

            // Setup media recorder
            const stream = canvas.captureStream(FRAME_RATE);
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'video/webm;codecs=vp9',
                videoBitsPerSecond: 5000000
            });

            chunksRef.current = [];
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                setGeneratedVideo({
                    url,
                    thumbnail: canvas.toDataURL('image/jpeg'),
                    duration: params.duration,
                    quality: params.quality,
                    aspectRatio: params.aspectRatio
                });
                setIsGenerating(false);
                setVideoProgress(100);
            };

            // Start recording
            mediaRecorderRef.current.start();

            // Generate speech
            const utterance = new SpeechSynthesisUtterance(params.prompt);
            utterance.rate = 0.9;
            utterance.pitch = 1;
            speechSynthesisRef.current.speak(utterance);

            // Animation loop
            let startTime = Date.now();
            const animate = () => {
                const elapsed = (Date.now() - startTime) / 1000;
                currentFrame++;

                // Clear canvas
                ctx.fillStyle = '#000000';
                ctx.fillRect(0, 0, width, height);

                // Calculate current word index
                const wordIndex = Math.floor(elapsed / WORD_DURATION) % words.length;
                const word = words[wordIndex];

                // Draw text with animation
                ctx.fillStyle = '#ffffff';
                ctx.font = `${height / 10}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Add some visual effects
                const progress = (elapsed % WORD_DURATION) / WORD_DURATION;
                const scale = 1 + Math.sin(progress * Math.PI) * 0.2;

                ctx.save();
                ctx.translate(width / 2, height / 2);
                ctx.scale(scale, scale);
                ctx.fillText(word, 0, 0);
                ctx.restore();

                // Add some particle effects
                const particles = 50;
                for (let i = 0; i < particles; i++) {
                    const angle = (i / particles) * Math.PI * 2 + elapsed;
                    const radius = Math.sin(elapsed * 2) * 100 + 150;
                    const x = width / 2 + Math.cos(angle) * radius;
                    const y = height / 2 + Math.sin(angle) * radius;

                    ctx.fillStyle = `hsla(${(angle + elapsed) * 50}, 70%, 60%, 0.5)`;
                    ctx.beginPath();
                    ctx.arc(x, y, 3, 0, Math.PI * 2);
                    ctx.fill();
                }

                // Update progress
                setVideoProgress((currentFrame / totalFrames) * 100);

                // Continue animation or stop
                if (currentFrame < totalFrames) {
                    requestAnimationFrame(animate);
                } else {
                    mediaRecorderRef.current.stop();
                    speechSynthesisRef.current.cancel();
                }
            };

            animate();

        } catch (error) {
            console.error('Error generating video:', error);
            setError('Failed to generate video. Please try again.');
            setIsGenerating(false);
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            speechSynthesisRef.current.cancel();
        }
    };

    const handleGenerateVideo = async () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt for the video');
            return;
        }

        setIsGenerating(true);
        setError(null);
        setVideoProgress(0);
        setGeneratedVideo(null);

        try {
            await generateVideo({
                prompt,
                quality: videoQuality,
                aspectRatio,
                duration,
            });
        } catch (err) {
            setError('Failed to generate video. Please try again.');
            console.error('Video generation error:', err);
            setIsGenerating(false);
        }
    };

    const handleDownload = () => {
        if (generatedVideo?.url) {
            const link = document.createElement('a');
            link.href = generatedVideo.url;
            link.download = `ai-generated-video-${Date.now()}.webm`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleOpenPopup = () => {
        setOpenPopup(true);
    };

    const handleClosePopup = () => {
        setOpenPopup(false);
    };

    // Cleanup function
    useEffect(() => {
        return () => {
            if (generatedVideo?.url) {
                URL.revokeObjectURL(generatedVideo.url);
            }
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            speechSynthesisRef.current.cancel();
        };
    }, [generatedVideo]);

    return (
        <Box className="chatseaction1">
            <Box className="video-gen-section">
                <Box className="page-content">
                    <Box className="header">
                        <Link to="/" style={{ textDecoration: 'none' }}>
                            <button className="back-link">
                                <ArrowBackIcon className="back-icon" />
                                Back to Chat
                            </button>
                        </Link>

                        <Box className="header-content">
                            <Typography variant="h1">AI Video Generator (Coming Soon)</Typography>
                            <Typography variant="body1">
                                Create animated videos from text prompts
                            </Typography>
                        </Box>
                        
                        {/* <Darkmode /> */}
                    </Box>

                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                    <Box className="main-content">
                        <Card className="input-card">
                            <CardContent>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    label="Enter your video prompt"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="Describe the video you want to generate in detail..."
                                    className="prompt-input"
                                />

                                <Box className="options-container">
                                    <FormControl className="quality-select">
                                        <InputLabel>Video Quality</InputLabel>
                                        <Select
                                            value={videoQuality}
                                            onChange={(e) => setVideoQuality(e.target.value)}
                                            label="Video Quality"
                                        >
                                            {VIDEO_QUALITIES.map((quality) => (
                                                <MenuItem key={quality.value} value={quality.value}>
                                                    {quality.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl className="aspect-select">
                                        <InputLabel>Aspect Ratio</InputLabel>
                                        <Select
                                            value={aspectRatio}
                                            onChange={(e) => setAspectRatio(e.target.value)}
                                            label="Aspect Ratio"
                                        >
                                            {ASPECT_RATIOS.map((ratio) => (
                                                <MenuItem key={ratio.value} value={ratio.value}>
                                                    {ratio.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box className="duration-slider">
                                    <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center">
                                        <TimerIcon sx={{ mr: 1 }} />
                                        Video Duration: {duration} seconds
                                    </Typography>
                                    <Slider
                                        value={duration}
                                        onChange={(e, newValue) => setDuration(newValue)}
                                        min={60}
                                        max={120}
                                        step={1}
                                        marks={[
                                            { value: 60, label: '1:00' },
                                            { value: 90, label: '1:30' },
                                            { value: 120, label: '2:00' },
                                        ]}
                                        valueLabelDisplay="auto"
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={handleOpenPopup}
                                    // onClick={handleGenerateVideo}
                                    // disabled={isGenerating || !prompt.trim()}
                                    className="generate-btn"
                                >
                                    Generate Video
                                    {/* {isGenerating ? (
                                        <>
                                            <CircularProgress size={24} color="white" />
                                            <span style={{ marginLeft: '10px' }}>
                                                Generating... {videoProgress}%
                                            </span>
                                        </>
                                    ) : (
                                        'Generate Video'
                                    )} */}
                                </Button>

                                {/* <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={handleGenerateVideo}
                                    disabled={isGenerating || !prompt.trim()}
                                    className="generate-btn" */}

                                <Dialog
                                    open={openPopup}
                                    onClose={handleClosePopup}
                                    aria-labelledby="coming-soon-dialog"
                                    PaperProps={{
                                        style: {
                                            backgroundColor: '#2a2a2a',
                                            color: '#ffffff',
                                            borderRadius: '16px',
                                            padding: '1rem'
                                        }
                                    }}
                                >

                                    {/* {isGenerating ? (
                                        <>
                                            <CircularProgress size={24} color="white" />
                                            <span style={{ marginLeft: '10px' }}>
                                                Generating... {videoProgress}%
                                            </span>
                                        </>
                                    ) : (
                                        'Generate Video'
                                    )}
                                </Button>

                                {error && (
                                    <Alert severity="error" className="error-alert">
                                        {error}
                                    </Alert>
                                )} */}

                                    <DialogTitle sx={{ color: '#ffffff' }}>
                                        Coming Soon!
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText sx={{ color: '#a0aec0' }}>
                                            Video generation feature is currently under development. Stay tuned for amazing AI-powered video creation!
                                        </DialogContentText>
                                    </DialogContent>
                                </Dialog>
                            </CardContent>
                        </Card>

                        {generatedVideo && (
                            <Card className="result-card">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Generated Video
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Duration: {duration} seconds |
                                        Quality: {videoQuality} |
                                        Aspect Ratio: {aspectRatio}
                                    </Typography>
                                    <Box className="video-container">
                                        <video
                                            controls
                                            poster={generatedVideo.thumbnail}
                                            className="generated-video"
                                            style={{
                                                aspectRatio: generatedVideo.aspectRatio.replace(':', '/'),
                                            }}
                                        >
                                            <source src={generatedVideo.url} type="video/webm" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        startIcon={<CloudDownloadIcon />}
                                        onClick={handleDownload}
                                        className="download-btn"
                                    >
                                        Download Video
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default AiVideoGen;